import { Button, Cartel, DropdownMenu, Price, StatusDot } from '@artosera/ui';
import { Input, Select, Switch } from '@artosera/ui';
import {
  Archive,
  CloudUpload,
  HardDrive,
  Image as ImageIcon,
  KeyRound,
  Plus,
  Search,
  Store,
  TriangleAlert,
  WifiOff,
} from 'lucide-react';
import type { Devise, StatutOeuvre } from '@artosera/shared';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLieux } from '../../api/lieux.js';
import { useOeuvres } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { useShellContext } from '../shell/context.js';
import { AjouterOeuvresStandDialog } from './AjouterOeuvresStandDialog.js';
import {
  NouveauStandDialog,
  NouvelleFoireDialog,
  peutAjouterDesOeuvres,
  peutCreerFoire,
  peutCreerStand,
} from './CreationStand.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { couleurDuStatut } from '../oeuvres/statutUi.js';
import { useFileHorsLigneFoire } from './useFileHorsLigneFoire.js';
import styles from './StandPage.module.css';

const CLE_PRIX_DISCRETS = 'artosera-foire-prix-discrets';

function lirePrixDiscrets(): boolean {
  try {
    return window.localStorage.getItem(CLE_PRIX_DISCRETS) === '1';
  } catch {
    return false;
  }
}

/**
 * Mode foire — grille du stand (3.3, D5, S1-c, ARTO-4). Plein écran
 * tablette, tuiles tactiles ≥ 44 px, prix en très grand ou masqués d'un
 * geste (« prix discrets » pour montrer l'écran à un visiteur).
 */
function useTotalDuStand(
  galerieSlug: string,
  foireActive: string,
  statut?: StatutOeuvre,
): number | undefined {
  const { data } = useOeuvres(
    galerieSlug,
    {
      localisationId: foireActive,
      ...(statut ? { statut: [statut] } : {}),
      parPage: 1,
      tri: 'recent',
    },
    { enabled: Boolean(foireActive) },
  );
  return data?.pagination.total;
}

export function StandPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'oeuvres', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { galerieSlug, galerie } = useShellContext();

  const { data: lieux } = useLieux(galerieSlug);
  const standsFoire = useMemo(() => (lieux ?? []).filter((lieu) => lieu.type === 'foire'), [lieux]);
  const [foireId, setFoireId] = useState('');
  const foireActive = foireId || (standsFoire[0]?.id ?? '');
  const standActif = standsFoire.find((stand) => stand.id === foireActive) ?? null;

  const [creation, setCreation] = useState<'foire' | 'stand' | null>(null);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const peutStand = peutCreerStand(galerie);
  const peutFoire = peutCreerFoire(galerie);
  const peutAjouter = peutAjouterDesOeuvres(galerie);

  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [prixDiscrets, setPrixDiscrets] = useState(lirePrixDiscrets);

  const {
    enLigne,
    enAttente,
    actionsEnQuarantaine,
    stockageMemoire,
    arretRejeu,
    actionsAVerifier,
  } = useFileHorsLigneFoire(galerieSlug);

  const { data, isLoading } = useOeuvres(
    galerieSlug,
    {
      localisationId: foireActive,
      ...(rechercheDebattue ? { q: rechercheDebattue } : {}),
      parPage: 200,
      tri: 'recent',
    },
    { enabled: Boolean(foireActive) },
  );

  // Les compteurs disent tout le stand, pas seulement ce que la recherche ou
  // la première page en gardent : on lit le total de chaque statut.
  const totalStand = useTotalDuStand(galerieSlug, foireActive);
  const totaux = {
    available: useTotalDuStand(galerieSlug, foireActive, 'disponible'),
    reserved: useTotalDuStand(galerieSlug, foireActive, 'reservee'),
    sold: useTotalDuStand(galerieSlug, foireActive, 'vendue'),
  };

  const basculerPrixDiscrets = (valeur: boolean): void => {
    setPrixDiscrets(valeur);
    try {
      window.localStorage.setItem(CLE_PRIX_DISCRETS, valeur ? '1' : '0');
    } catch {
      /* stockage indisponible : le choix ne survit pas au rechargement */
    }
  };

  const oeuvres = data?.oeuvres ?? [];
  const compteurs = (['available', 'reserved', 'sold'] as const).map((statut) => ({
    statut,
    nombre: totaux[statut] ?? 0,
  }));
  const standVide = totalStand === 0;
  const aucunStand = lieux !== undefined && standsFoire.length === 0;

  return (
    <div className={styles.page} data-testid="stand-page">
      {/* État de la file hors ligne : un bandeau par situation, teinté selon
          sa gravité — l'attente (ambre) n'appelle aucun geste, les alertes
          (rouge) demandent d'agir avant de quitter le stand. */}
      {!enLigne ||
      enAttente.length > 0 ||
      actionsAVerifier > 0 ||
      arretRejeu ||
      stockageMemoire ||
      actionsEnQuarantaine > 0 ? (
        <div className={styles.bandeaux}>
          {!enLigne || enAttente.length > 0 ? (
            <div className={`${styles.bandeau} ${styles.bandeauAttente}`} role="status">
              {!enLigne ? (
                <WifiOff className={styles.bandeauIcone} aria-hidden="true" size={20} />
              ) : (
                <CloudUpload className={styles.bandeauIcone} aria-hidden="true" size={20} />
              )}
              <span>
                {!enLigne
                  ? t('regie:foire.horsLigne.hors_ligne')
                  : t('regie:foire.horsLigne.enAttente', { count: enAttente.length })}
              </span>
            </div>
          ) : null}
          {actionsAVerifier > 0 ? (
            <div className={`${styles.bandeau} ${styles.bandeauAlerte}`} role="alert">
              <TriangleAlert className={styles.bandeauIcone} aria-hidden="true" size={20} />
              <span>{t('regie:foire.horsLigne.aVerifier', { count: actionsAVerifier })}</span>
            </div>
          ) : null}
          {arretRejeu ? (
            <div className={`${styles.bandeau} ${styles.bandeauAlerte}`} role="alert">
              <KeyRound className={styles.bandeauIcone} aria-hidden="true" size={20} />
              <span>{t(`regie:foire.horsLigne.arret.${arretRejeu}`)}</span>
            </div>
          ) : null}
          {stockageMemoire ? (
            <div className={`${styles.bandeau} ${styles.bandeauAlerte}`} role="alert">
              <HardDrive className={styles.bandeauIcone} aria-hidden="true" size={20} />
              <span>{t('regie:foire.horsLigne.stockageMemoire')}</span>
            </div>
          ) : null}
          {actionsEnQuarantaine > 0 ? (
            <div className={`${styles.bandeau} ${styles.bandeauAlerte}`} role="alert">
              <Archive className={styles.bandeauIcone} aria-hidden="true" size={20} />
              <span>{t('regie:foire.horsLigne.quarantaine', { count: actionsEnQuarantaine })}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* L'enseigne du stand : la foire en cours à gauche, les outils du
          vendeur à droite. Collée en haut pour rester sous la main pendant
          qu'on parcourt l'accrochage. */}
      <div className={styles.enseigne}>
        <div className={styles.foireChoix}>
          <span className={styles.foireLabel}>
            <Store aria-hidden="true" size={14} />
            {t('regie:foire.foireEnCours')}
          </span>
          <Select
            aria-label={t('regie:foire.foireEnCours')}
            value={foireActive}
            onValueChange={setFoireId}
            placeholder={t('regie:foire.aucuneFoire')}
            options={standsFoire.map((stand) => ({ value: stand.id, label: stand.nom }))}
          />
        </div>

        <div className={styles.outils}>
          <div className={styles.rechercheChamp}>
            <Search className={styles.rechercheIcone} aria-hidden="true" size={18} />
            <Input
              className={styles.recherche}
              type="search"
              placeholder={t('regie:foire.rechercheChamp')}
              aria-label={t('regie:foire.rechercheChamp')}
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </div>
          {peutFoire || peutStand || (peutAjouter && standActif) ? (
            <DropdownMenu
              trigger={
                <Button variant="secondary" className={styles.plus}>
                  <Plus size={18} strokeWidth={1.5} aria-hidden />
                  {t('regie:foire.creation.menu')}
                </Button>
              }
              entries={[
                ...(peutFoire
                  ? [
                      {
                        type: 'item' as const,
                        item: {
                          key: 'foire',
                          label: t('regie:foire.creation.nouvelleFoire'),
                          onSelect: () => setCreation('foire'),
                        },
                      },
                    ]
                  : []),
                ...(peutStand
                  ? [
                      {
                        type: 'item' as const,
                        item: {
                          key: 'stand',
                          label: t('regie:foire.creation.nouveauStand'),
                          onSelect: () => setCreation('stand'),
                        },
                      },
                    ]
                  : []),
                ...(peutAjouter && standActif
                  ? [
                      {
                        type: 'item' as const,
                        item: {
                          key: 'oeuvres',
                          label: t('regie:foire.creation.ajouterOeuvres'),
                          onSelect: () => setAjoutOuvert(true),
                        },
                      },
                    ]
                  : []),
              ]}
            />
          ) : null}
          <div className={styles.basculePrix} data-actif={prixDiscrets || undefined}>
            <Switch
              id="foire-prix-discrets"
              label={t('regie:foire.prixDiscrets')}
              checked={prixDiscrets}
              onCheckedChange={basculerPrixDiscrets}
            />
          </div>
        </div>
      </div>

      {standActif ? (
        <ul className={styles.compteurs} aria-label={t('regie:foire.compteurs.libelle')}>
          {compteurs.map(({ statut, nombre }) => (
            <li key={statut} className={styles.compteur} data-statut={statut}>
              <strong className={styles.compteurNombre}>{nombre}</strong>
              <span>
                {t(
                  `regie:foire.compteurs.${statut === 'available' ? 'disponible' : statut === 'reserved' ? 'reservee' : 'vendue'}`,
                  { count: nombre },
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {data && oeuvres.length < data.pagination.total ? (
        <p role="status" className={styles.chargement}>
          {t('regie:foire.listeTronquee', {
            count: oeuvres.length,
            affiches: oeuvres.length,
            total: data.pagination.total,
          })}
        </p>
      ) : null}

      {isLoading ? (
        <p className={styles.chargement}>
          {t('commun:chargement', { defaultValue: 'Chargement…' })}
        </p>
      ) : oeuvres.length === 0 ? (
        <div className={styles.vide}>
          <span className={styles.videCadre} aria-hidden="true" />
          <p>
            {aucunStand && (peutFoire || peutStand)
              ? t('regie:foire.creation.aucunStand')
              : t('regie:foire.grilleVide')}
          </p>
          {aucunStand && peutFoire ? (
            <Button onClick={() => setCreation('foire')}>
              <Plus size={18} strokeWidth={1.5} aria-hidden />
              {t('regie:foire.creation.nouvelleFoire')}
            </Button>
          ) : null}
          {aucunStand && peutStand ? (
            <Button variant="secondary" onClick={() => setCreation('stand')}>
              {t('regie:foire.creation.nouveauStand')}
            </Button>
          ) : null}
          {standVide && !rechercheDebattue && peutAjouter && standActif ? (
            <Button onClick={() => setAjoutOuvert(true)}>
              <Plus size={18} strokeWidth={1.5} aria-hidden />
              {t('regie:foire.creation.ajouterOeuvres')}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className={styles.accrochage} data-ui="stand-grid">
          {oeuvres.map((oeuvre) => (
            <StandTuile
              key={oeuvre.id}
              galerieSlug={galerieSlug}
              oeuvre={oeuvre}
              langue={langue}
              prixDiscrets={prixDiscrets}
              prixMasqueLabel={t('regie:foire.prixMasque')}
              onOuvrir={() => navigate(oeuvre.id)}
            />
          ))}
        </div>
      )}

      {creation === 'stand' ? (
        <NouveauStandDialog
          galerieSlug={galerieSlug}
          onOpenChange={(ouvert) => !ouvert && setCreation(null)}
          onStandCree={(stand) => setFoireId(stand.id)}
        />
      ) : null}
      {creation === 'foire' ? (
        <NouvelleFoireDialog
          galerieSlug={galerieSlug}
          onOpenChange={(ouvert) => !ouvert && setCreation(null)}
          onStandCree={(stand) => setFoireId(stand.id)}
        />
      ) : null}
      {ajoutOuvert && standActif ? (
        <AjouterOeuvresStandDialog
          galerieSlug={galerieSlug}
          stand={standActif}
          onFerme={() => setAjoutOuvert(false)}
        />
      ) : null}
    </div>
  );
}

interface OeuvreDeLaTuile {
  readonly id: string;
  readonly titre: string;
  readonly artisteNom: string;
  readonly annee: number | null;
  readonly statut: StatutOeuvre;
  readonly prixPublicCentimes: number | null;
  readonly devise: Devise;
  readonly mediaPrincipalId: string | null;
}

interface StandTuileProps {
  galerieSlug: string;
  oeuvre: OeuvreDeLaTuile;
  langue: 'fr' | 'en';
  prixDiscrets: boolean;
  prixMasqueLabel: string;
  onOuvrir: () => void;
}

/**
 * Tuile du stand (ARTO-53, § 5) : l'image de l'œuvre (variante d'aperçu),
 * plutôt qu'une icône générique pour toutes les œuvres (audit du
 * 23 septembre 2026, § 3 — « Afficher les œuvres est indispensable avant de
 * présenter le mode foire »).
 */
function StandTuile({
  galerieSlug,
  oeuvre,
  langue,
  prixDiscrets,
  prixMasqueLabel,
  onOuvrir,
}: StandTuileProps): JSX.Element {
  const { t } = useTranslation(['oeuvres']);
  const apercu = useApercuMedia(galerieSlug, oeuvre.mediaPrincipalId);

  const statut = couleurDuStatut(oeuvre.statut);

  return (
    <button type="button" className={styles.tuile} data-statut={statut} onClick={onOuvrir}>
      {/* La cimaise : l'œuvre accrochée sur son pan de mur, comme sur le stand. */}
      <span className={styles.cimaise} aria-hidden="true">
        {apercu.data ? (
          <img className={styles.oeuvreAccrochee} src={apercu.data} alt="" />
        ) : (
          <span className={styles.cadreVide}>
            <ImageIcon size={28} />
          </span>
        )}
      </span>
      <span className={styles.statut}>
        <StatusDot status={statut} lang={langue} label={t(`oeuvres:statuts.${oeuvre.statut}`)} />
      </span>
      <Cartel
        className={styles.cartel}
        artist={oeuvre.artisteNom}
        title={oeuvre.titre}
        year={oeuvre.annee ?? undefined}
      />
      {oeuvre.prixPublicCentimes !== null ? (
        <span className={styles.prixLigne}>
          {prixDiscrets ? (
            <span className={styles.prixMasque} aria-label={prixMasqueLabel}>
              •••
            </span>
          ) : (
            <Price
              className={styles.prix}
              cents={oeuvre.prixPublicCentimes}
              lang={langue}
              currency={oeuvre.devise}
              size="lg"
            />
          )}
        </span>
      ) : null}
    </button>
  );
}
